import { SimpleToggle } from "components/Form/components/Switch/Switch";
import { urlValidator } from "components/Form/Form.utils";
import { Icon } from "components/Icon/Icon";
import { IconCollection } from "components/Icon/IconCollection";
import { ResourceConfig } from "config/config";
import { normalizeNullableStringFields } from "config/resourceUtils";
import {
    deleteActionSignature,
    DeleteListAction,
    deletePermanentlyActionSignature,
    duplicateActionSignature,
    exportActionSignature,
    moveToLocationActionSignature,
    PermanentlyDeleteListAction,
} from "containers/Resources/components/CommonActions/CommonActions";
import { makeTestConnectionActionSignature } from "containers/Resources/components/CommonActions/testConnectionAction";
import {
    commentsTab,
    logsTab,
    logsTabKey,
    metadataTab,
} from "containers/Resources/screens/ResourcesSingle/constants";
import {
    buildSimpleColumn,
    isEnabledColumn,
} from "containers/Search/components/SearchTabs/columns";
import resource from "./resource";
import { getUrlDTO, isEnabledField } from "./sharedFields";

const resourceName = "vault_config";

const config: ResourceConfig = {
    label: "Vault config",
    icon: IconCollection.safeSquareOutline,
    resourceName,
    resourceClass: "resource",
    defaultActiveTab: logsTabKey,
    layout: [
        { partial: "pageHeaderForm" },
        { partial: "form" },
        {
            partial: "tabs",
            config: {
                tabs: [logsTab.tab, metadataTab.tab, commentsTab.tab],
                content: [logsTab.content, metadataTab.content, commentsTab.content],
            },
        },
    ],
    actions: {
        shown: [makeTestConnectionActionSignature({ resourceName })],
        hidden: [
            duplicateActionSignature,
            exportActionSignature,
            moveToLocationActionSignature,
            deleteActionSignature,
            deletePermanentlyActionSignature,
        ],
    },
    listActions: {
        shown: [],
        hidden: [
            { key: "delete", action: DeleteListAction },
            { key: "deletePermanently", action: PermanentlyDeleteListAction },
        ],
    },
    graphql: {
        resource: resourceName,
        resourceBody: `
            ${resource.graphql.resourceBody}
            is_enabled
            vault_url
            engine_path
            server_ca
            verify_ssl
            client_cert
            has_client_key
            check_hostname
            username
            has_password
            is_auto_renew_enabled
            has_token
        `,
        resourceBodyListing: `
            ${resource.graphql.resourceBody}
            is_enabled
            vault_url
            check_hostname
            engine_path
            has_client_key
            has_password
            has_token
            is_auto_renew_enabled
            verify_ssl
        `,
        createVariables: `
            ${resource.graphql.createVariables(resourceName)}
            $is_enabled: Boolean,
            $vault_url: String,
            $engine_path: String,
            $server_ca: String,
            $verify_ssl: Boolean,
            $client_cert: String,
            $client_key: String,
            $check_hostname: Boolean,
            $username: String,
            $password: String,
            $is_auto_renew_enabled: Boolean,
            $token: String,
        `,
        createQuery: `
            ${resource.graphql.createQuery(resourceName)}
            is_enabled: $is_enabled,
            vault_url: $vault_url,
            engine_path: $engine_path,
            server_ca: $server_ca,
            verify_ssl: $verify_ssl,
            client_cert: $client_cert,
            client_key: $client_key,
            check_hostname: $check_hostname,
            username: $username,
            password: $password,
            is_auto_renew_enabled: $is_auto_renew_enabled,
            token: $token,
        `,
        updateVariables: `
            ${resource.graphql.updateVariables(resourceName)}
            $is_enabled: Boolean,
            $vault_url: String,
            $engine_path: String,
            $server_ca: String,
            $verify_ssl: Boolean,
            $client_cert: String,
            $client_key: String,
            $check_hostname: Boolean,
            $username: String,
            $password: String,
            $is_auto_renew_enabled: Boolean,
            $token: String,
        `,
        updateQuery: `
            ${resource.graphql.updateQuery(resourceName)}
            is_enabled: $is_enabled,
            vault_url: $vault_url,
            engine_path: $engine_path,
            server_ca: $server_ca,
            verify_ssl: $verify_ssl,
            client_cert: $client_cert,
            client_key: $client_key,
            check_hostname: $check_hostname,
            username: $username,
            password: $password,
            is_auto_renew_enabled: $is_auto_renew_enabled,
            token: $token,
        `,
    },
    additionalColumns: (setNewValue) => [
        isEnabledColumn(setNewValue),
        {
            label: "Vault URL",
            key: "vault_url",
            shown: true,
            sortable: true,
            dto: (value: any) =>
                buildSimpleColumn(
                    getUrlDTO(value.vault_url),
                    setNewValue
                        ? () => setNewValue({ field: "vault_url", value: value.vault_url })
                        : undefined
                ),
        },
        {
            label: "Check hostname",
            key: "check_hostname",
            shown: true,
            sortable: true,
            dto: (value: any) =>
                buildSimpleColumn(
                    <SimpleToggle
                        identifier="check_hostname"
                        value={value.check_hostname}
                        isReadOnly={true}
                    />,
                    setNewValue
                        ? () =>
                              setNewValue({ field: "check_hostname", value: value.check_hostname })
                        : undefined
                ),
        },
        {
            label: "Engine path",
            key: "engine_path",
            shown: true,
            sortable: true,
            dto: (value: any) =>
                buildSimpleColumn(
                    getUrlDTO(value.engine_path),
                    setNewValue
                        ? () => setNewValue({ field: "engine_path", value: value.engine_path })
                        : undefined
                ),
        },
        {
            label: "Has client key",
            key: "has_client_key",
            shown: true,
            sortable: true,
            dto: (value: any) =>
                value?.has_client_key
                    ? buildSimpleColumn(
                          <Icon icon={IconCollection.check} />,
                          setNewValue
                              ? () => setNewValue({ field: "has_client_key", value: true })
                              : undefined
                      )
                    : null,
        },
        {
            label: "Has password",
            key: "has_password",
            shown: true,
            sortable: true,
            dto: (value: any) =>
                value?.has_password
                    ? buildSimpleColumn(
                          <Icon icon={IconCollection.check} />,
                          setNewValue
                              ? () => setNewValue({ field: "has_password", value: true })
                              : undefined
                      )
                    : null,
        },
        {
            label: "Has token",
            key: "has_token",
            shown: true,
            sortable: true,
            dto: (value: any) =>
                value?.has_token
                    ? buildSimpleColumn(
                          <Icon icon={IconCollection.check} />,
                          setNewValue
                              ? () => setNewValue({ field: "has_token", value: true })
                              : undefined
                      )
                    : null,
        },
        {
            label: "Auto renew enabled",
            key: "is_auto_renew_enabled",
            shown: true,
            sortable: true,
            dto: (value: any) =>
                buildSimpleColumn(
                    <SimpleToggle
                        identifier="is_auto_renew_enabled"
                        value={value.is_auto_renew_enabled}
                        isReadOnly={true}
                    />,
                    setNewValue
                        ? () =>
                              setNewValue({
                                  field: "is_auto_renew_enabled",
                                  value: value.is_auto_renew_enabled,
                              })
                        : undefined
                ),
        },
        {
            label: "Verify SSL",
            key: "verify_ssl",
            shown: true,
            sortable: true,
            dto: (value: any) =>
                buildSimpleColumn(
                    <SimpleToggle
                        identifier="verify_ssl"
                        value={value.verify_ssl}
                        isReadOnly={true}
                    />,
                    setNewValue
                        ? () =>
                              setNewValue({
                                  field: "verify_ssl",
                                  value: value.verify_ssl,
                              })
                        : undefined
                ),
        },
    ],
    form: [
        isEnabledField,
        {
            name: "is_auto_renew_enabled",
            component: "switch",
            fieldProps: {
                label: "Auto-Renew",
                docs: {
                    text: "Auto renew token",
                    url: "/docs/vault-integration",
                },
            },
        },
        {
            name: "vault_url",
            component: "url",
            fieldProps: {
                label: "Vault URL",
                docs: {
                    text: "URL of Vault",
                    url: "/docs/vault-integration",
                },
            },
            validation: urlValidator,
        },
        {
            name: "server_ca",
            component: "textarea",
            fieldProps: {
                label: "CA certificate",
                docs: {
                    text: "The content of the server's CA certificates in PEM format. To be used for self-signed certificates.",
                    url: "/docs/vault-integration",
                },
            },
        },
        {
            name: "verify_ssl",
            component: "switch",
            fieldProps: {
                label: "Verify SSL",
                docs: {
                    text: "Verify the server's SSL certificate. **Strongly recommended**. Can be disabled if using a self-signed certificate.",
                    url: "/docs/vault-integration",
                },
            },
        },
        {
            name: "client_cert",
            component: "textarea",
            fieldProps: {
                label: "Client certificate",
                docs: {
                    text: "A client certificate used to authenticate the SSL transport.",
                    url: "/docs/vault-integration",
                },
            },
        },
        {
            name: "client_key",
            component: "writeOnlyInput",
            fieldProps: {
                label: "Client certificate key",
                clearable: true,
                docs: {
                    text: "The key of the client certificate used to authenticate the SSL transport.",
                    url: "/docs/vault-integration",
                },
                isMultiLine: true,
            },
            fieldAttr: {
                placeholder: "Set new client certificate key",
            },
        },
        {
            name: "check_hostname",
            component: "switch",
            fieldProps: {
                label: "Check hostname",
                docs: {
                    text: "If set, the hostname of the server is checked against the CA certificate.",
                    url: "/docs/vault-integration",
                },
            },
        },
        {
            name: "username",
            component: "text",
            fieldProps: {
                label: "Username",
                docs: {
                    text: "The username to authenticate",
                    url: "/docs/vault-integration",
                },
            },
            fieldAttr: {
                autoComplete: "new-password",
            },
        },
        {
            name: "password",
            component: "writeOnlyInput",
            fieldProps: {
                label: "Password",
                clearable: true,
                docs: {
                    text: "Password for authentication",
                    url: "/docs/vault-integration",
                },
            },
            fieldAttr: {
                placeholder: "Set new password",
            },
        },
        {
            name: "engine_path",
            component: "text",
            fieldProps: {
                label: "Engine path",
                docs: {
                    text: "The engine path, often `secret` or `kv`",
                    url: "/docs/vault-integration",
                },
            },
        },
        {
            name: "token",
            component: "writeOnlyInput",
            fieldProps: {
                label: "Token",
                clearable: true,
                docs: {
                    text: "Token for authentication",
                    url: "/docs/vault-integration",
                },
            },
            fieldAttr: {
                placeholder: "Set new token",
            },
        },
    ],
    formDTO: {
        initialValueDTO: (resourceData: any) =>
            normalizeNullableStringFields(resourceData, ["server_ca", "client_cert"]),
    },
};
export default config;
